import React, { useEffect, useState, useMemo } from 'react';
import { jwtDecode } from 'jwt-decode';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FiEye, FiEyeOff, FiUser, FiLock, FiSliders, FiCpu } from 'react-icons/fi';
import { useTranslation, Trans } from 'react-i18next';
import BASE_URL from '../../config/config';
import FotoPerfil from '../../components/ui/FotoPerfil';
import Pestanas from '../../components/ui/Pestanas';
import IntegracionesIA from './perfil/IntegracionesIA';
import DispositivosConectados from './perfil/DispositivosConectados';
import FilaAjuste from '../../components/ui/FilaAjuste';
import SelectorIdioma from '../../components/ui/SelectorIdioma';
import CodigoVerificacion from '../../components/ui/CodigoVerificacion';
import { resolverIdioma } from '../../i18n/idiomas';
import { sincronizarIdiomaConSesion, cambiarIdioma } from '../../i18n';
import { normalizarTelefonoInternacional, validarTelefonoInternacional } from '../../utils/telefono';
import '../../css/perfil.css';
import { cerrarSesion } from '../../auth/sesion';

const Perfil = () => {
  const token = localStorage.getItem('AuthToken');
  const tokenInfo = token ? jwtDecode(token) : {};
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Valores de partida: lo que había guardado al entrar. Se congelan en el
  // montaje y solo se mueven al guardar, porque son la referencia contra la que
  // se decide qué está pendiente. En concreto el idioma NO puede recalcularse:
  // `resolverIdioma()` lee el localStorage que el propio selector acaba de
  // escribir al pulsar, así que la diferencia se anulaba sola y el cambio no
  // llegaba nunca a la cuenta.
  const [iniciales, setIniciales] = useState(() => ({
    nombre: tokenInfo.nombre || '',
    apellidos: tokenInfo.apellidos || '',
    email: tokenInfo.email || '',
    telefono: tokenInfo.telefono || '',
    webs_del_cliente: tokenInfo.webs_del_cliente || '',
    idioma: tokenInfo.idioma || resolverIdioma(),
  }));

  const [pestanaActiva, setPestanaActiva] = useState('informacion');
  const [showPass, setShowPass] = useState(false);
  const [showPassRep, setShowPassRep] = useState(false);
  const [nombre, setNombre] = useState(iniciales.nombre);
  const [apellidos, setApellidos] = useState(iniciales.apellidos);
  const [email, setEmail] = useState(iniciales.email);
  const [telefono, setTelefono] = useState(iniciales.telefono);
  const [contrasena, setContrasena] = useState('');
  const [repetir, setRepetir] = useState('');
  const [webs_del_cliente, setWebs_del_cliente] = useState(iniciales.webs_del_cliente);
  // El idioma se aplica al pulsarlo (respuesta inmediata) y viaja al servidor
  // al guardar, que es lo que lo hace válido en cualquier dispositivo.
  const [idioma, setIdioma] = useState(iniciales.idioma);
  const [guardando, setGuardando] = useState(false);
  const [mfaActivo, setMfaActivo] = useState(null);
  const [mfaConfiguracion, setMfaConfiguracion] = useState(null);
  const [mfaCodigo, setMfaCodigo] = useState('');
  const [procesandoMfa, setProcesandoMfa] = useState(false);
  // Desactivar pide un código: el formulario solo aparece cuando se pide
  const [desactivandoMfa, setDesactivandoMfa] = useState(false);
  // true: no se pide el código en los navegadores de confianza
  const [mfaRecordar, setMfaRecordar] = useState(false);
  const [diasConfianza, setDiasConfianza] = useState(90);
  // Sube cuando algo de esta pantalla cambia la lista de dispositivos
  const [recargaDispositivos, setRecargaDispositivos] = useState(0);

  useEffect(() => {
    if (!token) return;
    axios.get(`${BASE_URL}/usuario/mfa/estado`, { headers: { Authorization: token } })
      .then(({ data }) => {
        setMfaActivo(data.activo);
        setMfaRecordar(Boolean(data.recordar_dispositivos));
        if (data.dias_confianza) setDiasConfianza(data.dias_confianza);
      })
      .catch(() => setMfaActivo(false));
  }, [token]);

  // Los errores se agrupan por pestaña: guardar Preferencias no puede quedar
  // bloqueado porque falte un apellido en Información.
  const erroresPorPestana = useMemo(() => {
    const informacion = {};
    if (!nombre.trim()) informacion.nombre = t('perfil.errores.nombre');
    if (!apellidos.trim()) informacion.apellidos = t('perfil.errores.apellidos');
    if (!email.trim()) informacion.email = t('perfil.errores.emailObligatorio');
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) informacion.email = t('perfil.errores.emailInvalido');
    if (telefono.trim() && !validarTelefonoInternacional(telefono).valido) informacion.telefono = t('perfil.errores.telefonoInvalido');

    const seguridad = {};
    if (contrasena) {
      if (contrasena.length < 6) seguridad.contrasena = t('perfil.errores.contrasenaCorta');
      if (contrasena !== repetir) seguridad.repetir = t('perfil.errores.contrasenasNoCoinciden');
    } else if (repetir) {
      seguridad.contrasena = t('perfil.errores.contrasenaPrimero');
    }

    return { informacion, seguridad, preferencias: {} };
  }, [nombre, apellidos, email, telefono, contrasena, repetir, t]);

  // Los campos siguen leyendo `errors` para pintarse en rojo: la unión sirve
  // para eso, pero nunca para decidir si se puede guardar.
  const errors = useMemo(
    () => ({ ...erroresPorPestana.informacion, ...erroresPorPestana.seguridad }),
    [erroresPorPestana],
  );

  // Cambios pendientes por pestaña: alimentan el contador de cada pestaña y
  // la barra de guardado, para no perder de vista lo editado en otra sección.
  const pendientes = useMemo(() => {
    const informacion = [
      nombre !== iniciales.nombre,
      apellidos !== iniciales.apellidos,
      email !== iniciales.email,
      telefono !== iniciales.telefono,
      webs_del_cliente !== iniciales.webs_del_cliente,
    ].filter(Boolean).length;
    return {
      informacion,
      seguridad: contrasena || repetir ? 1 : 0,
      preferencias: idioma !== iniciales.idioma ? 1 : 0,
    };
  }, [nombre, apellidos, email, telefono, webs_del_cliente, contrasena, repetir, idioma, iniciales]);

  const pendientesPestana = pendientes[pestanaActiva] || 0;

  // Vista previa de cómo se leen las webs: una por dominio, sin repetidos
  const dominios = useMemo(
    () => [...new Set(webs_del_cliente.split(',').map((d) => d.trim()).filter(Boolean))],
    [webs_del_cliente],
  );

  const nombreCompleto = [nombre, apellidos].filter(Boolean).join(' ') || tokenInfo.usuario;
  const esStaff = tokenInfo.role === 'admin' || tokenInfo.role === 'tecnico';

  const pestanas = [
    { id: 'informacion', label: t('perfil.tabInformacion'), icono: FiUser, pendientes: pendientes.informacion },
    { id: 'seguridad', label: t('perfil.tabSeguridad'), icono: FiLock, pendientes: pendientes.seguridad },
    { id: 'preferencias', label: t('perfil.tabPreferencias'), icono: FiSliders, pendientes: pendientes.preferencias },
    // El servidor MCP solo responde a admin y técnicos: a un cliente la pestaña
    // no le serviría de nada, así que ni se muestra.
    ...(esStaff ? [{ id: 'integraciones', label: t('perfil.tabIntegraciones'), icono: FiCpu }] : []),
  ];
  const etiquetaRol = tokenInfo.role === 'admin'
    ? t('menu.administrador')
    : tokenInfo.role === 'tecnico' ? t('menu.tecnico') : t('gestorUsuarios.roles.user');

  // Descarta solo la pestaña visible: lo editado en otra sigue ahí, marcado
  // con su contador, en vez de desaparecer sin avisar.
  const descartarCambios = () => {
    if (pestanaActiva === 'informacion') {
      setNombre(iniciales.nombre);
      setApellidos(iniciales.apellidos);
      setEmail(iniciales.email);
      setTelefono(iniciales.telefono);
      setWebs_del_cliente(iniciales.webs_del_cliente);
    }
    if (pestanaActiva === 'seguridad') {
      setContrasena('');
      setRepetir('');
    }
    if (pestanaActiva === 'preferencias') {
      setIdioma(iniciales.idioma);
      cambiarIdioma(iniciales.idioma);
    }
  };

  const handleLogout = () => {
    cerrarSesion({ caducada: false });
  };

  const iniciarMfa = async () => {
    try {
      setProcesandoMfa(true);
      const { data } = await axios.post(`${BASE_URL}/usuario/mfa/iniciar`, {}, { headers: { Authorization: token } });
      setMfaConfiguracion(data);
      setMfaCodigo('');
    } catch (error) {
      toast.error(t('perfil.mfa.errorIniciar'));
    } finally {
      setProcesandoMfa(false);
    }
  };

  const confirmarMfa = async () => {
    try {
      setProcesandoMfa(true);
      await axios.post(`${BASE_URL}/usuario/mfa/confirmar`, {
        configuracion_token: mfaConfiguracion.configuracion_token,
        codigo: mfaCodigo,
      }, { headers: { Authorization: token } });
      setMfaActivo(true);
      setMfaConfiguracion(null);
      setMfaCodigo('');
      toast.success(t('perfil.mfa.activado'));
    } catch (error) {
      toast.error(error.response?.data?.message || t('perfil.mfa.errorCodigo'));
    } finally {
      setProcesandoMfa(false);
    }
  };

  const desactivarMfa = async () => {
    try {
      setProcesandoMfa(true);
      await axios.delete(`${BASE_URL}/usuario/mfa`, { headers: { Authorization: token }, data: { codigo: mfaCodigo } });
      setMfaActivo(false);
      setMfaRecordar(false);
      setDesactivandoMfa(false);
      setMfaCodigo('');
      setRecargaDispositivos((n) => n + 1);
      toast.success(t('perfil.mfa.desactivado'));
    } catch (error) {
      toast.error(error.response?.data?.message || t('perfil.mfa.errorCodigo'));
    } finally {
      setProcesandoMfa(false);
    }
  };

  const cambiarRecordarMfa = async (recordar) => {
    if (recordar === mfaRecordar) return;
    const anterior = mfaRecordar;
    setMfaRecordar(recordar);
    try {
      await axios.put(`${BASE_URL}/usuario/mfa/recordar`, { recordar }, { headers: { Authorization: token } });
      // Al dejar de recordar, el servidor olvida todos los navegadores de confianza
      if (!recordar) setRecargaDispositivos((n) => n + 1);
      toast.success(t('perfil.mfa.preferenciaGuardada'));
    } catch {
      setMfaRecordar(anterior);
      toast.error(t('perfil.mfa.errorPreferencia'));
    }
  };

  /**
   * Guarda SOLO la pestaña indicada: valida su ámbito y envía sus campos. El
   * endpoint ignora las claves que no lleguen, así que un envío parcial no pisa
   * lo que se esté editando en otra pestaña.
   */
  const guardarPestana = async (pestana) => {
    const errores = erroresPorPestana[pestana] || {};
    if (Object.keys(errores).length > 0) {
      toast.error(t('perfil.revisaCampos'));
      return;
    }

    const cambiaContrasena = pestana === 'seguridad' && Boolean(contrasena);
    let datos = {};
    if (pestana === 'informacion') {
      datos = {
        nombre,
        apellidos,
        email,
        telefono: normalizarTelefonoInternacional(telefono),
        webs_del_cliente,
      };
    } else if (pestana === 'preferencias') {
      datos = { idioma };
    } else if (cambiaContrasena) {
      // La contraseña viaja en claro como 'nueva_contrasena': el hasheo lo hace
      // el servidor, igual que al crear un usuario o al resetearla.
      datos = { nueva_contrasena: contrasena };
    }

    if (Object.keys(datos).length === 0) return;

    try {
      setGuardando(true);
      const response = await axios.put(
        `${BASE_URL}/usuario/update/${tokenInfo.usuario}`,
        datos,
        { headers: { Authorization: token } }
      );
      if (response.status !== 200) {
        toast.error(t('perfil.errorGuardado'));
        return;
      }

      toast.success(t('perfil.guardado'));

      if (cambiaContrasena) {
        handleLogout();
        return;
      }

      // Lo guardado pasa a ser la nueva referencia de "sin cambios"
      setIniciales(prev => ({
        ...prev,
        ...(pestana === 'informacion' ? {
          nombre, apellidos, email,
          telefono: normalizarTelefonoInternacional(telefono),
          webs_del_cliente,
        } : {}),
        ...(pestana === 'preferencias' ? { idioma } : {}),
      }));
      if (pestana === 'informacion') setTelefono(normalizarTelefonoInternacional(telefono));

      if (response.data?.token) {
        localStorage.setItem('AuthToken', response.data.token);
        // El token nuevo ya trae el idioma guardado: la app se resincroniza
        window.dispatchEvent(new Event('auth-token-changed'));
        sincronizarIdiomaConSesion();
      }
    } catch (error) {
      console.error('Error al actualizar el perfil:', error);
      toast.error(t('perfil.errorGenerico'));
    } finally {
      setGuardando(false);
    }
  };

  const propsPanel = (id) => ({
    id: `panel-${id}`,
    role: 'tabpanel',
    'aria-labelledby': `tab-${id}`,
    className: 'perfil-panel',
    hidden: pestanaActiva !== id,
  });

  return (
    <main className="site-width perfil-page">
      <button className="boton-volver" onClick={() => navigate('/dashboard')}>
        ← {t('comun.volver')}
      </button>

      <div className="perfil-cabecera">
        <div className="perfil-avatar">
          <FotoPerfil />
        </div>
        <div className="perfil-identidad">
          <h1>{nombreCompleto}</h1>
          <p className="perfil-usuario">@{tokenInfo.usuario}</p>
          <ul className="perfil-chips">
            <li className={`perfil-chip perfil-chip--${esStaff ? tokenInfo.role : 'user'}`}>{etiquetaRol}</li>
            {email && <li className="perfil-chip">{email}</li>}
          </ul>
        </div>
        <p className="perfil-hint">{t('perfil.hint')}</p>
      </div>

      <div className="perfil-card">
        <Pestanas
          pestanas={pestanas}
          activa={pestanaActiva}
          onCambiar={setPestanaActiva}
          etiqueta={t('perfil.seccionesPerfil')}
        />

        <section {...propsPanel('informacion')}>
          <FilaAjuste titulo={t('perfil.filas.nombreTitulo')} texto={t('perfil.filas.nombreTexto')}>
            <div className="fila-ajuste-pareja">
              <div className="campo-ajuste">
                <label htmlFor="nombre">{t('comun.nombre')}</label>
                <input type="text" id="nombre" autoComplete="given-name" value={nombre} onChange={e => setNombre(e.target.value)} className={errors.nombre ? 'input-error' : ''} />
                {errors.nombre && <p className="campo-error">{errors.nombre}</p>}
              </div>
              <div className="campo-ajuste">
                <label htmlFor="apellidos">{t('comun.apellidos')}</label>
                <input type="text" id="apellidos" autoComplete="family-name" value={apellidos} onChange={e => setApellidos(e.target.value)} className={errors.apellidos ? 'input-error' : ''} />
                {errors.apellidos && <p className="campo-error">{errors.apellidos}</p>}
              </div>
            </div>
          </FilaAjuste>

          <FilaAjuste titulo={t('perfil.filas.contactoTitulo')} texto={t('perfil.filas.contactoTexto')}>
            <div className="campo-ajuste">
              <label htmlFor="email">{t('comun.email')}</label>
              <input type="email" id="email" autoComplete="email" value={email} onChange={e => setEmail(e.target.value)} className={errors.email ? 'input-error' : ''} />
              {errors.email && <p className="campo-error">{errors.email}</p>}
            </div>
            <div className="campo-ajuste">
              <label htmlFor="telefono">{t('comun.telefono')}</label>
              <input type="tel" inputMode="tel" autoComplete="tel" id="telefono" placeholder={t('perfilUsuario.telefonoInternacionalPlaceholder')} value={telefono} onChange={e => setTelefono(e.target.value)} onBlur={() => setTelefono(normalizarTelefonoInternacional(telefono))} className={errors.telefono ? 'input-error' : ''} aria-describedby="telefono-ayuda" />
              {errors.telefono
                ? <p className="campo-error">{errors.telefono}</p>
                : <p className="campo-ayuda" id="telefono-ayuda">{t('perfilUsuario.telefonoInternacionalHint')}</p>}
            </div>
          </FilaAjuste>

          <FilaAjuste titulo={t('perfil.filas.websTitulo')} texto={t('perfil.filas.websTexto')}>
            <div className="campo-ajuste">
              <label htmlFor="webs_del_cliente">{t('perfil.filas.websEtiqueta')}</label>
              <input type="text" id="webs_del_cliente" value={webs_del_cliente} onChange={e => setWebs_del_cliente(e.target.value)} placeholder={t('perfil.filas.websPlaceholder')} aria-describedby="webs-ayuda" />
              <p className="campo-ayuda" id="webs-ayuda">{t('perfil.sitiosMantenimientoHint')}</p>
            </div>
            {dominios.length > 0 && (
              <ul className="perfil-dominios" aria-label={t('perfil.filas.websLista')}>
                {dominios.map((dominio) => <li key={dominio}>{dominio}</li>)}
              </ul>
            )}
          </FilaAjuste>
        </section>

        <section {...propsPanel('seguridad')}>
          <FilaAjuste
            titulo={t('perfil.filas.usuarioTitulo')}
            texto={t('perfil.filas.usuarioTexto')}
          >
            <p className="perfil-valor-fijo">@{tokenInfo.usuario}</p>
            <p className="campo-ayuda">
              <Trans i18nKey="perfil.problemasUsuario" components={{ enlace: <a href="tel:+34856901276" /> }} />
            </p>
          </FilaAjuste>

          <FilaAjuste titulo={t('perfil.filas.contrasenaTitulo')} texto={t('perfil.filas.contrasenaTexto')}>
            <div className="campo-ajuste">
              <label htmlFor="contrasena">{t('perfil.filas.nuevaContrasena')}</label>
              <div className={`input-with-toggle ${errors.contrasena ? 'input-error' : ''}`}>
                <input
                  type={showPass ? 'text' : 'password'}
                  id="contrasena"
                  value={contrasena}
                  onChange={e => setContrasena(e.target.value)}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="toggle-visibility"
                  onClick={() => setShowPass(v => !v)}
                  aria-label={showPass ? t('comun.ocultarContrasena') : t('comun.mostrarContrasena')}
                  aria-pressed={showPass}
                  title={showPass ? t('comun.ocultarContrasena') : t('comun.mostrarContrasena')}
                >
                  {showPass ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
              {errors.contrasena && <p className="campo-error">{errors.contrasena}</p>}
            </div>

            <div className="campo-ajuste">
              <label htmlFor="repetir-contrasena">{t('perfil.repetirContrasena')}</label>
              <div className={`input-with-toggle ${errors.repetir ? 'input-error' : ''}`}>
                <input
                  type={showPassRep ? 'text' : 'password'}
                  id="repetir-contrasena"
                  value={repetir}
                  onChange={e => setRepetir(e.target.value)}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="toggle-visibility"
                  onClick={() => setShowPassRep(v => !v)}
                  aria-label={showPassRep ? t('comun.ocultarContrasena') : t('comun.mostrarContrasena')}
                  aria-pressed={showPassRep}
                  title={showPassRep ? t('comun.ocultarContrasena') : t('comun.mostrarContrasena')}
                >
                  {showPassRep ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
              {errors.repetir && <p className="campo-error">{errors.repetir}</p>}
            </div>

            {contrasena && !errors.contrasena && !errors.repetir && (
              <p className="perfil-nota perfil-nota--ok">{t('perfil.filas.coinciden')}</p>
            )}
            {contrasena && <p className="perfil-nota">{t('perfil.filas.avisoCierre')}</p>}
          </FilaAjuste>

          <FilaAjuste titulo={t('perfil.mfa.titulo')} texto={t('perfil.mfa.texto')}>
            {mfaActivo === null && <p className="campo-ayuda">{t('comun.cargando')}</p>}

            {mfaActivo !== null && !mfaConfiguracion && (
              <p className={`perfil-estado${mfaActivo ? ' perfil-estado--activo' : ''}`}>
                {mfaActivo ? t('perfil.mfa.estadoActiva') : t('perfil.mfa.estadoInactiva')}
              </p>
            )}

            {mfaActivo === false && !mfaConfiguracion && (
              <button type="button" className="boton-primario perfil-boton" onClick={iniciarMfa} disabled={procesandoMfa}>
                {t('perfil.mfa.configurar')}
              </button>
            )}

            {mfaConfiguracion && (
              <ol className="perfil-pasos">
                <li>
                  <p>{t('perfil.mfa.paso1')}</p>
                  <div className="perfil-qr">
                    <span className="perfil-qr-codigo">
                      <img src={mfaConfiguracion.qr} alt={t('perfil.mfa.qrAlt')} />
                      <img src="/images/faviconadhoc.png" alt="" aria-hidden="true" className="perfil-qr-logo" />
                    </span>
                    <div>
                      <span>{t('perfil.mfa.pasoClave')}</span>
                      <code className="perfil-clave">{mfaConfiguracion.secreto.match(/.{1,4}/g).join(' ')}</code>
                    </div>
                  </div>
                </li>
                <li>
                  <label htmlFor="codigo-mfa">{t('perfil.mfa.paso2')}</label>
                  <CodigoVerificacion id="codigo-mfa" compacto autoFocus value={mfaCodigo} onChange={setMfaCodigo} disabled={procesandoMfa} />
                  <div className="perfil-acciones">
                    <button type="button" className="boton-primario perfil-boton" onClick={confirmarMfa} disabled={procesandoMfa || mfaCodigo.length !== 6}>{t('perfil.mfa.activar')}</button>
                    <button type="button" className="perfil-boton-descartar" onClick={() => { setMfaConfiguracion(null); setMfaCodigo(''); }} disabled={procesandoMfa}>{t('comun.cancelar')}</button>
                  </div>
                </li>
              </ol>
            )}

            {mfaActivo && !mfaConfiguracion && (
              <>
                <fieldset className="perfil-opciones">
                  <legend>{t('perfil.mfa.cuandoPedir')}</legend>
                  <label className="perfil-opcion">
                    <input type="radio" name="mfa-frecuencia" checked={!mfaRecordar} onChange={() => cambiarRecordarMfa(false)} />
                    <span>
                      <strong>{t('perfil.mfa.siempre')}</strong>
                      <small>{t('perfil.mfa.siempreTexto')}</small>
                    </span>
                  </label>
                  <label className="perfil-opcion">
                    <input type="radio" name="mfa-frecuencia" checked={mfaRecordar} onChange={() => cambiarRecordarMfa(true)} />
                    <span>
                      <strong>{t('perfil.mfa.recordar')}</strong>
                      <small>{t('perfil.mfa.recordarTexto', { dias: diasConfianza })}</small>
                    </span>
                  </label>
                </fieldset>

                {!desactivandoMfa ? (
                  <button type="button" className="perfil-enlace-peligro" onClick={() => { setDesactivandoMfa(true); setMfaCodigo(''); }}>
                    {t('perfil.mfa.abrirDesactivar')}
                  </button>
                ) : (
                  <div className="perfil-desactivar">
                    <label htmlFor="codigo-mfa-desactivar">{t('perfil.mfa.codigoDesactivar')}</label>
                    <CodigoVerificacion id="codigo-mfa-desactivar" compacto autoFocus value={mfaCodigo} onChange={setMfaCodigo} disabled={procesandoMfa} />
                    <div className="perfil-acciones">
                      <button type="button" className="perfil-boton-peligro" onClick={desactivarMfa} disabled={procesandoMfa || mfaCodigo.length !== 6}>{t('perfil.mfa.desactivar')}</button>
                      <button type="button" className="perfil-boton-descartar" onClick={() => { setDesactivandoMfa(false); setMfaCodigo(''); }} disabled={procesandoMfa}>{t('comun.cancelar')}</button>
                    </div>
                  </div>
                )}
              </>
            )}
          </FilaAjuste>

          <FilaAjuste titulo={t('perfil.dispositivos.titulo')} texto={t('perfil.dispositivos.texto')}>
            <DispositivosConectados token={token} recarga={recargaDispositivos} />
          </FilaAjuste>
        </section>

        <section {...propsPanel('preferencias')}>
          <FilaAjuste titulo={t('idioma.etiqueta')} texto={t('idioma.ayudaPerfil')}>
            <SelectorIdioma variante="campo" value={idioma} onCambio={setIdioma} />
          </FilaAjuste>
        </section>

        {esStaff && (
          <section {...propsPanel('integraciones')}>
            <IntegracionesIA onAviso={(tipo, texto) => (tipo === 'error' ? toast.error(texto) : toast.success(texto))} />
          </section>
        )}
      </div>

      {/* Barra de guardado: solo aparece con cambios, y sigue visible aunque
          se cambie de pestaña, para no dejar ediciones olvidadas detrás. */}
      {pendientesPestana > 0 && (
        <div className="perfil-barra-guardado" role="status">
          <span className="perfil-barra-texto">
            {t('perfil.cambiosSinGuardarEn', {
              count: pendientesPestana,
              seccion: pestanas.find(p => p.id === pestanaActiva)?.label || '',
            })}
          </span>
          <div className="perfil-barra-acciones">
            <button type="button" className="perfil-boton-descartar" onClick={descartarCambios} disabled={guardando}>
              {t('perfil.descartar')}
            </button>
            <button
              type="button"
              className="boton-primario"
              onClick={() => guardarPestana(pestanaActiva)}
              disabled={guardando || Object.keys(erroresPorPestana[pestanaActiva] || {}).length > 0}
            >
              {guardando ? t('perfil.guardando') : t('perfil.guardarSeccion')}
            </button>
          </div>
        </div>
      )}

      <ToastContainer position="bottom-right" autoClose={3000} />
    </main>
  );
};

export default Perfil;
